import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Inbox, Loader2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CollapsibleSection } from "@/components/settings/SettingsUI";
import { getApprovalQueue, approvePO, rejectPO, approveChangeOrder } from "@/lib/api";
import { FoundationSyncPill } from "./FoundationSyncPill";

const POLL_MS = 30_000;
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function CountChip({ label, n, drafts, testId }) {
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${n > 0 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid={testId}>
      {label} {n}{drafts ? <span className="font-normal text-slate-400"> (+{drafts} draft)</span> : null}
    </span>
  );
}

function DocRow({ row, onApprove, onReject, busy }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const isPO = row.kind === "purchase_order";
  return (
    <div className={`border rounded-lg p-3 ${row.is_draft ? "border-dashed border-slate-200 bg-slate-50/60" : "border-slate-200 bg-white"}`} data-testid={`approval-row-${row.kind}-${row.id}`} data-wo={row.wo_id}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{isPO ? "Purchase order" : "Change order"}</span>
            <span className="font-mono text-xs text-slate-800">{row.number}</span>
            <span className="text-xs text-slate-500">WO {row.wo_number || "—"}{row.client_name ? ` · ${row.client_name}` : ""}</span>
            <span className="text-xs font-medium tabular-nums text-slate-700">{money(row.total)}</span>
            {isPO && row.vendor && <span className="text-xs text-slate-500">· {row.vendor}</span>}
            {!isPO && row.title && <span className="text-xs text-slate-500 truncate max-w-[220px]">· {row.title}</span>}
          </div>
          <div className="text-[11px]" data-testid={`approval-reason-${row.kind}-${row.id}`}>
            <span className={row.is_draft ? "text-slate-500" : "text-amber-800 font-medium"}>{row.reason}</span>
            <span className="text-slate-400"> · {row.is_draft ? "created" : "submitted"} {when(row.submitted_at || row.created_at)}{row.submitted_by ? ` by ${row.submitted_by}` : ""}</span>
          </div>
          {row.foundation_sync && <div className="mt-1"><FoundationSyncPill sync={row.foundation_sync} testId={`approval-sync-${row.id}`} /></div>}
        </div>
        {!row.is_draft && (
          <div className="flex flex-col gap-1.5 shrink-0">
            <Button size="sm" onClick={() => onApprove(row)} disabled={busy} className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approval-approve-${row.kind}-${row.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
            </Button>
            {isPO && (
              <Button size="sm" variant="outline" onClick={() => setRejecting(r => !r)} disabled={busy} className="h-7 px-2.5 text-xs text-rose-700 border-rose-300 hover:bg-rose-50" data-testid={`approval-reject-${row.kind}-${row.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" /> Send back
              </Button>
            )}
          </div>
        )}
      </div>
      {rejecting && (
        <div className="mt-2 pt-2 border-t border-slate-200 flex gap-1.5 items-center">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the buyer (optional)" className="h-7 text-xs" data-testid={`approval-reject-reason-${row.id}`} />
          <Button size="sm" className="h-7 px-2 text-xs bg-rose-600 hover:bg-rose-700 text-white" onClick={() => { onReject(row, reason); setRejecting(false); }} data-testid={`approval-reject-confirm-${row.id}`}>Confirm</Button>
        </div>
      )}
    </div>
  );
}

// SVC-FIX-2 — everything a person must approve, the moment it needs approval (POs · COs · Foundation pushes).
export function ApprovalQueueSection({ pushRows, renderPushRow, loadingPush, onCountChange }) {
  const [data, setData] = useState(null);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const reload = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const r = await getApprovalQueue(includeDrafts);
      setData(r.data);
      setLastLoadedAt(new Date());
      onCountChange && onCountChange(r.data?.counts?.total || 0);
    } catch (e) {
      if (manual) toast.error(e?.response?.data?.detail || "Could not load the approval queue");
    } finally { if (manual) setRefreshing(false); }
  }, [includeDrafts, onCountChange]);

  useEffect(() => {
    reload();
    const t = setInterval(() => reload(), POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      const r = row.kind === "purchase_order" ? await approvePO(row.wo_id, row.id) : await approveChangeOrder(row.wo_id, row.id);
      toast.success(r.data?.message || "Approved", { duration: 6000 });
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Not approved: the server could not be reached"); }
    finally { setBusyId(null); }
  };
  const reject = async (row, reason) => {
    setBusyId(row.id);
    try {
      const r = await rejectPO(row.wo_id, row.id, reason);
      toast.success(r.data?.message || "Sent back");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the PO back"); }
    finally { setBusyId(null); }
  };

  const c = data?.counts || {};
  const docs = [...(data?.purchase_orders || []), ...(data?.change_orders || [])];
  const total = (c.total || 0);
  const anyRows = docs.length > 0 || (pushRows || []).length > 0;

  return (
    <CollapsibleSection
      id="foundation-pending" testId="foundation-pending-section"
      icon={Inbox} iconClass="text-amber-600"
      title={`Pending Approval — Change Orders · AR · POs (${total})`}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <CountChip label="POs" n={c.purchase_orders || 0} drafts={includeDrafts ? 0 : c.purchase_orders_drafts} testId="approval-count-pos" />
          <CountChip label="COs" n={c.change_orders || 0} drafts={includeDrafts ? 0 : c.change_orders_drafts} testId="approval-count-cos" />
          <CountChip label="Foundation Pushes" n={c.foundation_pushes || 0} testId="approval-count-pushes" />
          <label className="text-[11px] text-slate-600 inline-flex items-center gap-1 cursor-pointer" data-testid="approval-include-drafts">
            <input type="checkbox" checked={includeDrafts} onChange={(e) => setIncludeDrafts(e.target.checked)} className="h-3 w-3" data-testid="approval-include-drafts-toggle" /> include drafts
          </label>
          {lastLoadedAt && <span className="text-[10px] text-slate-400" data-testid="foundation-last-refreshed">Updated {lastLoadedAt.toLocaleTimeString()}</span>}
          <button onClick={() => reload(true)} disabled={refreshing} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="foundation-refresh">
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      }
    >
      {data?.threshold > 0 && <div className="text-[11px] text-slate-500 mb-2" data-testid="approval-threshold-note">POs under {money(data.threshold)} are approved automatically on submit (Settings → PO approval threshold).</div>}
      {(!data && loadingPush) ? (
        <div className="text-center py-6 text-sm text-slate-400" data-testid="foundation-pending-loading">Loading…</div>
      ) : !anyRows ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center text-sm text-emerald-700" data-testid="foundation-pending-empty">
          <CheckCircle2 className="w-5 h-5 inline mr-2 -mt-0.5" />
          No Change Orders, AR invoices or POs waiting for approval.
          {(c.purchase_orders_drafts || c.change_orders_drafts) && !includeDrafts ? <div className="text-xs text-emerald-600 mt-1">{c.purchase_orders_drafts || 0} PO draft(s) · {c.change_orders_drafts || 0} CO draft(s) not yet submitted — tick “include drafts” to see them.</div> : null}
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(row => <DocRow key={`${row.kind}-${row.wo_id}-${row.id}`} row={row} onApprove={approve} onReject={reject} busy={busyId === row.id} />)}
          {(pushRows || []).map(renderPushRow)}
        </div>
      )}
    </CollapsibleSection>
  );
}
